'use client';
import { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import SectionHeader from '@/components/ui/SectionHeader';
import Accordion from '@/components/ui/Accordion';

interface Service {
  id: string;
  name: string;
  description: string;
  outcome: string;
  timeCost: string;
  bestFor: string;
}

interface ServiceCategory {
  id: string;
  title: string;
  description: string;
  services: Service[];
}

export default function ServicesMenuPage() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const serviceCategories: ServiceCategory[] = [
    {
      id: 'strategy-audits',
      title: 'Strategy & Bottleneck Audits',
      description: 'We start by diagnosing before prescribing. These engagements identify exactly where revenue is leaking, what to fix first, and what to stop doing.',
      services: [
        {
          id: 'strategy',
          name: 'Growth Bottleneck Audit',
          description: 'A structured diagnostic that maps your growth constraints, identifies revenue leaks, and delivers a prioritized execution roadmap. Includes market analysis, competitive research, channel planning, and a 90-day action plan.',
          outcome: 'Complete bottleneck analysis with channel plan, budget allocation, and 90-day prioritized roadmap.',
          timeCost: '20-35h',
          bestFor: 'Founders and operators who feel stuck, are scaling past $500K, or are unsure what to fix first',
        },
        {
          id: 'operational-strategy-consultation',
          name: 'Operational Strategy Consultation',
          description: 'Strategic operational consulting that identifies inefficiencies, unlocks capacity, and builds a roadmap for sustainable growth. Includes operational assessment, gap analysis, bottleneck identification, and 90-day roadmap.',
          outcome: 'Operational assessment with gap analysis, efficiency recommendations, and 90-day improvement roadmap.',
          timeCost: '15-30h',
          bestFor: 'Founders feeling stuck, businesses with shrinking margins, or teams preparing for a new growth phase',
        },
        {
          id: 'market-research',
          name: 'Deep/Wide Market Research',
          description: 'Comprehensive market research including market sizing, competitive landscape mapping, customer segment analysis, trend research, and pricing benchmarks. Delivered as a structured report with executive summary and recommendations.',
          outcome: 'Research report with market sizing, competitive landscape, customer analysis, and strategic recommendations.',
          timeCost: '20-40h',
          bestFor: 'Companies entering new markets, validating pricing or positioning, or making major strategic decisions',
        },
        {
          id: 'pricing-analysis',
          name: 'Pricing Analysis and Suggestion',
          description: 'Data-driven pricing analysis that benchmarks your rates against the market and recommends a pricing strategy to maximize revenue. Includes competitive benchmarks, value-based assessment, and implementation roadmap.',
          outcome: 'Pricing recommendations with competitive benchmarks, revenue projections, and implementation strategy.',
          timeCost: '10-20h',
          bestFor: 'Businesses unsure if they are priced correctly, launching new products, or preparing to raise prices',
        },
        {
          id: 'location-analysis',
          name: 'Location Analysis for Brick & Mortar',
          description: 'Comprehensive location intelligence for retail, restaurant, and brick-and-mortar businesses. Includes foot traffic data, demographic profiling, competitive density mapping, lease benchmarks, and scored site recommendations.',
          outcome: 'Scored location comparison with foot traffic data, demographics, competitive analysis, and final recommendation.',
          timeCost: '15-30h',
          bestFor: 'Retail and restaurant businesses scouting new locations or evaluating market expansion',
        },
      ],
    },
    {
      id: 'growth-sprints',
      title: 'Growth Sprints',
      description: 'Focused, time-boxed campaigns that generate measurable results fast. We identify the highest-leverage channel, build the campaign, and ship.',
      services: [
        {
          id: 'paid-search',
          name: 'Paid Search',
          description: 'Strategic Google Ads and Bing Ads campaigns designed to capture high-intent search traffic. Includes keyword research, ad copywriting, bid management, landing page optimization, and ongoing performance tracking.',
          outcome: 'Fully managed search campaigns with keyword targeting, ad creation, and conversion tracking.',
          timeCost: '10-16h',
          bestFor: 'Businesses looking to capture customers actively searching for their products or services',
        },
        {
          id: 'paid-social',
          name: 'Paid Social',
          description: 'Targeted social media advertising across Facebook, Instagram, LinkedIn, and TikTok. Includes audience research, creative development, campaign setup, A/B testing, and performance optimization.',
          outcome: 'Multi-platform social ad campaigns with audience targeting, creative assets, and optimization.',
          timeCost: '12-20h',
          bestFor: 'Brands wanting to reach specific audiences with compelling visual content and drive engagement',
        },
        {
          id: 'paid-media-management',
          name: 'Paid Media Management',
          description: 'Comprehensive multi-channel paid advertising management across Meta, Google, TikTok, and LinkedIn. Includes campaign strategy, ad creative production, audience research, budget allocation, A/B testing, and monthly strategy reviews.',
          outcome: 'Performance-driven paid media execution with multi-channel management and sprint-based optimization.',
          timeCost: '15-25h',
          bestFor: 'Scaling brands, performance-focused teams, and businesses with multi-channel paid media needs',
        },
        {
          id: 'seo',
          name: 'SEO',
          description: 'Comprehensive search engine optimization covering technical SEO, on-page optimization, content strategy, link building, and local SEO. Includes site audits, keyword research, and ongoing optimization.',
          outcome: 'Technical SEO audit, keyword strategy, on-page optimization, and 90-day implementation roadmap.',
          timeCost: '15-25h',
          bestFor: 'Companies wanting sustainable organic traffic growth and improved search visibility',
        },
        {
          id: 'geo-aeo',
          name: 'GEO | AEO',
          description: 'Generative Engine Optimization (GEO) and Answer Engine Optimization (AEO) to optimize content for AI-powered search engines like ChatGPT, Perplexity, and Google SGE.',
          outcome: 'Content optimization for AI search engines with structured data and answer-focused content.',
          timeCost: '12-20h',
          bestFor: 'Forward-thinking companies preparing for AI-powered search and voice assistant discovery',
        },
      ],
    },
    {
      id: 'implementation',
      title: 'Implementation & Buildouts',
      description: 'We build the infrastructure that supports growth — websites, funnels, brand systems, content engines, and creative assets that do real work.',
      services: [
        {
          id: 'web-design',
          name: 'Website & Funnel Build',
          description: 'Custom website design and development including UX/UI design, responsive development, CMS integration, and optimization. From landing pages to full website builds with conversion-focused architecture.',
          outcome: 'Custom website design with responsive development, CMS integration, and launch support.',
          timeCost: '40-80h',
          bestFor: 'Businesses needing a new website or complete redesign with modern UX and conversion focus',
        },
        {
          id: 'branding-design',
          name: 'Brand & Creative',
          description: 'Brand identity development and design services including logo design, brand guidelines, visual identity systems, and marketing collateral. Comprehensive brand strategy and execution.',
          outcome: 'Complete brand identity package with logo, guidelines, and marketing collateral designs.',
          timeCost: '30-50h',
          bestFor: 'New businesses or established companies looking to refresh their brand identity',
        },
        {
          id: 'content',
          name: 'Content Production',
          description: 'Strategic content creation including blog posts, articles, whitepapers, case studies, and website copy. Includes content strategy, SEO optimization, editorial calendar, and ongoing content production.',
          outcome: 'Content strategy with editorial calendar and professionally written, SEO-optimized content pieces.',
          timeCost: '8-15h',
          bestFor: 'Businesses needing consistent, high-quality content to drive organic traffic and establish authority',
        },
        {
          id: 'photo-video',
          name: 'Photo & Video',
          description: 'Professional photography and videography services including concept development, shooting, editing, and post-production. Shot in Las Vegas or on location with full production crew.',
          outcome: 'Professional photo/video content from concept through final delivery with full production support.',
          timeCost: '25-50h',
          bestFor: 'Brands needing high-quality visual content for campaigns, websites, and social media',
        },
        {
          id: 'pitch-deck-presentation',
          name: 'Pitch Deck & Presentation',
          description: 'Investor-ready pitch decks and executive presentations combining strategic storytelling with polished design. Includes narrative structure, copywriting, custom visuals, data visualization, and editable source files.',
          outcome: 'Professionally designed, narrative-driven deck with copywriting, visuals, and editable files in PowerPoint and PDF.',
          timeCost: '15-30h',
          bestFor: 'Founders raising funding, executives presenting to boards, and sales teams needing a high-converting leave-behind',
        },
      ],
    },
    {
      id: 'ongoing-execution',
      title: 'Ongoing Execution & Optimization',
      description: 'Consistent weekly execution across channels — not a one-time project, but a sustained growth engine that compounds over time.',
      services: [
        {
          id: 'growth-marketing',
          name: 'Growth Marketing',
          description: 'Full-funnel growth marketing execution including multi-channel strategy, conversion rate optimization, email and SMS automation, and continuous performance improvement.',
          outcome: 'Growth marketing engine with multi-channel execution, CRO, and automated nurture sequences.',
          timeCost: '25-40h',
          bestFor: 'Scaling companies that need a full-funnel growth partner to drive predictable, compounding revenue',
        },
        {
          id: 'lifecycle-marketing',
          name: 'Lifecycle Marketing',
          description: 'Customer lifecycle optimization including email automation, retention campaigns, loyalty programs, and customer journey mapping. Focus on maximizing customer lifetime value.',
          outcome: 'Lifecycle marketing strategy with automated campaigns, segmentation, and retention programs.',
          timeCost: '18-30h',
          bestFor: 'Subscription businesses and e-commerce companies focused on retention and LTV growth',
        },
        {
          id: 'social-media',
          name: 'Social Media Management',
          description: 'Full-service social media management including strategy, content creation, community management, posting schedule, engagement, and analytics. Organic and paid social integration.',
          outcome: 'Social media strategy with content calendar, daily management, and performance reporting.',
          timeCost: '15-25h',
          bestFor: 'Brands wanting consistent social presence with professional content and community engagement',
        },
        {
          id: 'affiliate-marketing',
          name: 'Performance Marketing',
          description: 'Performance-based partner program setup and management. Includes partner recruitment, commission structure design, tracking implementation, relationship management, and performance reporting.',
          outcome: 'Partner program setup with tracking, recruitment, and ongoing management.',
          timeCost: '20-30h',
          bestFor: 'E-commerce and service companies looking to scale through performance partnerships',
        },
        {
          id: 'reputation-management',
          name: 'Reputation Management (Birdeye)',
          description: 'Online reputation management using Birdeye platform including review generation, review monitoring, response management, sentiment analysis, and reputation reporting.',
          outcome: 'Birdeye setup with review automation, monitoring, response templates, and reporting dashboard.',
          timeCost: '10-18h',
          bestFor: 'Service businesses and multi-location companies needing proactive review and reputation management',
        },
        {
          id: 'public-relations',
          name: 'Public Relations',
          description: 'Strategic PR services including media relations, press release writing and distribution, media kit development, crisis management, and brand reputation building.',
          outcome: 'PR strategy with media outreach, press releases, and ongoing media relationship management.',
          timeCost: '15-25h',
          bestFor: 'Companies launching products, managing reputation, or seeking media coverage and brand awareness',
        },
      ],
    },
    {
      id: 'systems-automation',
      title: 'Systems, Automation & Internal Tools',
      description: 'We build the operational infrastructure that lets your business run without you in every decision — SOPs, org design, and internal systems.',
      services: [
        {
          id: 'internal-operational-structure',
          name: 'Internal Operational Structure',
          description: 'Organizational design and operational structure consulting that defines roles, reporting lines, and workflows. Includes current state assessment, org chart design, role definition documents, decision-making framework, and hiring roadmap.',
          outcome: 'Org chart with defined roles, reporting lines, decision framework, and 6-12 month hiring roadmap.',
          timeCost: '20-40h',
          bestFor: 'Founders scaling their team who need clear accountability, reporting structure, and operational clarity',
        },
        {
          id: 'internal-sop-assistance',
          name: 'Internal SOP Assistance',
          description: 'Standard Operating Procedure development that captures your processes, reduces errors, and makes training faster. Includes process discovery, SOP writing, flowcharts, and a documentation library.',
          outcome: 'Complete SOP library with step-by-step processes, flowcharts, and implementation guide.',
          timeCost: '15-30h',
          bestFor: "Businesses where key processes live in the founder's head and need to be documented for scale",
        },
        {
          id: 'amazon-services',
          name: 'Amazon Services',
          description: 'Complete Amazon marketplace optimization including listing optimization, Amazon Ads management, A+ content creation, inventory management, and review strategy.',
          outcome: 'Amazon storefront optimization with listing improvements, ad campaigns, and sales strategy.',
          timeCost: '20-35h',
          bestFor: 'E-commerce brands selling on Amazon who want to maximize visibility and conversions',
        },
        {
          id: 'media-buying',
          name: 'Media Buying',
          description: 'Strategic media planning and buying across digital and traditional channels. Includes audience analysis, media mix recommendations, negotiation, placement, and campaign performance tracking.',
          outcome: 'Media plan with channel recommendations, budget allocation, placement execution, and reporting.',
          timeCost: '15-25h',
          bestFor: 'Brands with significant ad budgets looking for strategic media planning and buying expertise',
        },
      ],
    },
    {
      id: 'diy-resources',
      title: 'DIY Resources & Specialized',
      description: 'Self-service tools, templates, and specialized one-off capabilities for businesses that want to move independently or need niche execution.',
      services: [
        {
          id: 'influencer-sourcing',
          name: 'Influencer Sourcing',
          description: 'Research-driven influencer identification and vetting across YouTube, Instagram, TikTok, and other platforms. Includes audience quality analysis, engagement rate review, brand safety vetting, and outreach templates.',
          outcome: 'Vetted influencer shortlist with profiles, metrics, audience analysis, and outreach templates.',
          timeCost: '15-25h',
          bestFor: 'Brands launching influencer marketing or needing vetted influencer lists for specific niches',
        },
        {
          id: 'out-of-home-marketing',
          name: 'Out of Home Marketing',
          description: 'Traditional outdoor advertising including billboard design and placement, transit advertising, street furniture ads, and experiential marketing campaigns.',
          outcome: 'OOH campaign strategy with creative design, media buying, and placement execution.',
          timeCost: '25-40h',
          bestFor: 'Local businesses and brands looking for high-visibility outdoor advertising in key markets',
        },
        {
          id: 'podcast-generation',
          name: 'Podcast Generation',
          description: 'End-to-end podcast production including concept development, branding, cover art, recording, editing, show notes, distribution setup, and social clips for each episode.',
          outcome: 'Fully produced podcast with branding, distribution on all major platforms, and social clips per episode.',
          timeCost: '20-40h',
          bestFor: 'Founders, coaches, and brands wanting a recurring content channel that builds audience and authority',
        },
      ],
    },
  ];

  const allServices = serviceCategories.flatMap(cat =>
    cat.services.map(service => ({ ...service, category: cat.title, categoryId: cat.id }))
  );

  const categoryFiltered = activeCategory === 'all'
    ? allServices
    : allServices.filter(service => service.categoryId === activeCategory);

  const filteredServices = searchQuery.trim() === ''
    ? categoryFiltered
    : categoryFiltered.filter(service => {
        const q = searchQuery.toLowerCase();
        return (
          service.name.toLowerCase().includes(q) ||
          service.description.toLowerCase().includes(q) ||
          service.category.toLowerCase().includes(q) ||
          service.outcome.toLowerCase().includes(q) ||
          service.bestFor.toLowerCase().includes(q)
        );
      });

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground">
        {/* Hero */}
        <section className="py-32 px-6 border-b border-border">
          <div className="max-w-[1400px] mx-auto text-center">
            <SectionHeader
              eyebrow="Service Architecture"
              title="Diagnose. Sprint. Build. Execute. Scale."
              description="Every engagement follows a deliberate sequence. We start by identifying the bottleneck, run a focused sprint to prove the model, build the infrastructure, then execute and optimize every week. This is not a menu of disconnected services — it is a machine."
              align="center"
            />
            <div className="mt-12 max-w-3xl mx-auto">
              <p className="text-body-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">How it works:</strong> Each service below shows typical time costs.
                These are scoped during sprint planning and drawn from your monthly capacity bucket.
                No surprises, no scope creep — just real work with real estimates.
              </p>
            </div>
          </div>
        </section>

        {/* Search + Filter Tabs */}
        <section className="py-3 px-6 border-b border-border sticky top-[73px] bg-background z-30">
          <div className="max-w-[1600px] mx-auto">
            {/* Search Input */}
            <div className="mb-4 max-w-xl mx-auto">
              <div className="relative">
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search services by name, category, or deliverable..."
                  className="w-full pl-11 pr-10 py-3 bg-surface-1 border border-border text-foreground placeholder-muted-foreground text-sm focus:outline-none focus:border-primary transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                    aria-label="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            {/* Category Filter Tabs */}
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-6 py-3 text-label uppercase tracking-widest font-medium transition-all duration-300 border ${
                  activeCategory === 'all' ? 'bg-primary text-background border-primary' : 'bg-surface-1 text-muted-foreground border-border hover:border-primary hover:text-foreground'
                }`}
              >
                All Services ({allServices.length})
              </button>
              {serviceCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-3 text-label uppercase tracking-widest font-medium transition-all duration-300 border ${
                    activeCategory === category.id
                      ? 'bg-primary text-background border-primary' : 'bg-surface-1 text-muted-foreground border-border hover:border-primary hover:text-foreground'
                  }`}
                >
                  {category.title} ({category.services.length})
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 px-6">
          <div className="max-w-[1600px] mx-auto">
            {filteredServices.length === 0 ? (
              <div className="text-center py-24">
                <div className="text-6xl mb-6">🔍</div>
                <h3 className="text-2xl font-bold text-white mb-3">No results found</h3>
                <p className="text-muted-foreground mb-6">
                  No services match &ldquo;<span className="text-white">{searchQuery}</span>&rdquo;. Try a different keyword or browse by category.
                </p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                  className="px-6 py-3 border border-primary text-primary hover:bg-primary hover:text-background transition-all text-label uppercase tracking-widest"
                >
                  Clear Search
                </button>
              </div>
            ) : (
              <>
                {searchQuery && (
                  <div className="mb-8 text-sm text-muted-foreground">
                    Showing <span className="text-white font-medium">{filteredServices.length}</span> result{filteredServices.length !== 1 ? 's' : ''} for &ldquo;<span className="text-primary">{searchQuery}</span>&rdquo;
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredServices.map((service) => (
                    <div
                      key={service.id}
                      className="p-8 border border-border hover:border-primary transition-all duration-300 bg-surface-1 group flex flex-col"
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <Badge variant="default" className="text-xs">
                          {service.category}
                        </Badge>
                        <Badge variant="primary" className="text-xs font-bold">
                          {service.timeCost}
                        </Badge>
                      </div>

                      {/* Service Name */}
                      <h3 className="text-h5 mb-4 group-hover:text-primary transition-colors">
                        {service.name}
                      </h3>

                      {/* Description */}
                      <p className="text-body-sm text-muted-foreground mb-4 leading-relaxed">
                        {service.description}
                      </p>

                      {/* Outcome */}
                      <div className="mb-4">
                        <p className="text-micro uppercase tracking-widest text-primary font-medium mb-2">
                          What You Get
                        </p>
                        <p className="text-body-sm text-foreground">
                          {service.outcome}
                        </p>
                      </div>

                      {/* Best For */}
                      <div className="mb-6 flex-grow">
                        <p className="text-micro uppercase tracking-widest text-primary font-medium mb-2">
                          Best For
                        </p>
                        <p className="text-body-sm text-muted-foreground">
                          {service.bestFor}
                        </p>
                      </div>

                      {/* CTA */}
                      <Link href="/book" className="mt-auto">
                        <Button
                          variant="secondary"
                          size="md"
                          className="w-full group-hover:bg-primary group-hover:text-background group-hover:border-primary transition-all"
                        >
                          Book Consultation
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Pricing Context Section */}
        <section className="py-24 px-6 border-t border-border bg-surface-1">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-h2 mb-6">How These Services Fit Into Pricing</h2>
              <p className="text-body text-muted-foreground max-w-3xl mx-auto">
                All services are billed against your monthly capacity bucket. Choose your engagement model,
                and we&apos;ll scope services in sprints to fit your budget and timeline.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="p-8 border border-border bg-background">
                <h3 className="text-h5 text-primary mb-3">Sprint-Based Scoping</h3>
                <p className="text-body-sm text-muted-foreground">
                  Every service is scoped during sprint planning. We estimate time costs, you approve,
                  and we execute. No hidden fees or surprise overages.
                </p>
              </div>
              <div className="p-8 border border-border bg-background">
                <h3 className="text-h5 text-primary mb-3">Capacity Bucket Model</h3>
                <p className="text-body-sm text-muted-foreground">
                  Your monthly bucket covers multiple services. Mix and match based on priorities.
                  Unused hours don&apos;t disappear—they roll into the next sprint.
                </p>
              </div>
              <div className="p-8 border border-border bg-background">
                <h3 className="text-h5 text-primary mb-3">Transparent Time Tracking</h3>
                <p className="text-body-sm text-muted-foreground">
                  Real-time visibility into hours used, remaining capacity, and project status.
                  You always know where you stand.
                </p>
              </div>
            </div>

            <div className="text-center">
              <Link href="/pricing">
                <Button variant="secondary" size="lg" className="inline-flex">
                  View Full Pricing Details
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-h2 mb-6">Frequently Asked Questions</h2>
              <p className="text-body text-muted-foreground max-w-2xl mx-auto">
                Everything you need to know about our capacity bucket model, sprint-based scoping,
                and how we work with clients.
              </p>
            </div>

            <Accordion
              items={[
                {
                  id: 'faq-1',
                  title: 'What are capacity buckets and how do they work?',
                  content: 'Capacity buckets are monthly hour allocations that give you flexible access to our services. We offer three tiers: Always-On (40-80 hours/month for ongoing execution needs), Growth (80-160 hours/month for scaling teams), and Projects (custom scoping for one-time initiatives). You choose your bucket size based on your workload, and we draw from it as we complete services. Unused hours roll into the next sprint, so nothing goes to waste. Think of it as a retainer with complete transparency and flexibility.',
                },
                {
                  id: 'faq-2',
                  title: 'How does sprint-based scoping work?',
                  content: 'Every two weeks, we hold a sprint planning session where you prioritize what needs to get done. We estimate the time cost for each service (shown on this page), you approve the scope, and we execute. At the end of the sprint, we review what shipped, discuss what is next, and plan the following sprint. This approach ensures you are always in control of priorities, budgets, and timelines. No surprises, no scope creep—just predictable execution.',
                },
                {
                  id: 'faq-3',
                  title: 'How is pricing determined?',
                  content: 'Pricing is based on your chosen capacity bucket and engagement model. Monthly retainers (Always-On, Growth) provide a fixed monthly cost with a set number of hours. Project-based engagements are scoped upfront with a total time estimate and fixed fee. All time costs are transparent—you see exactly how many hours each service requires before we start. We track time in real-time and provide full visibility into your remaining capacity. No hidden fees, no billing surprises.',
                },
                {
                  id: 'faq-4',
                  title: 'What are typical project timelines?',
                  content: 'Timelines vary by service complexity. Quick-turn services like social posts or website updates take 2-4 hours (often completed within 1-2 days). Mid-sized projects like email campaigns or SEO audits take 6-16 hours (typically 1-2 weeks). Larger initiatives like video production or website builds take 20-40 hours (2-4 weeks depending on revisions and feedback cycles). During sprint planning, we will provide specific delivery estimates based on your priorities and our current workload.',
                },
                {
                  id: 'faq-5',
                  title: 'Can I mix services from different categories?',
                  content: 'Absolutely. That is the beauty of the capacity bucket model. You can combine a Growth Bottleneck Audit with a Paid Search sprint and an SOP buildout all within the same engagement. Your bucket is flexible — use it however your business needs it. We will help you prioritize based on impact and urgency during sprint planning.',
                },
                {
                  id: 'faq-6',
                  title: 'What happens if a service takes longer than estimated?',
                  content: 'If scope changes during execution (new requirements, additional revisions, expanded deliverables), we pause, reassess the time cost, and get your approval before continuing. You are never charged for scope creep caused by our mistakes or inefficiencies — we eat that cost. But if you request changes beyond the original scope, we will transparently communicate the additional time required and adjust the sprint plan accordingly. You always stay in control of the budget.',
                },
                {
                  id: 'faq-7',
                  title: 'Do you bill hourly?',
                  content: 'No. We use a capacity bucket model, not hourly billing. You pay a fixed monthly retainer (or project fee) that covers a set number of hours. This eliminates the watching the clock mentality and aligns our incentives with yours — we focus on delivering results efficiently, not maximizing billable hours. You get predictable costs, and we get the freedom to work without micromanagement. It is a better model for both sides.',
                },
                {
                  id: 'faq-8',
                  title: 'How do I know what plan fits my needs?',
                  content: 'Start by estimating your monthly workload. If you need 1-2 services per month (social posts, website updates, occasional campaigns), Always-On (40-80 hours) is likely sufficient. If you are running multiple campaigns, building new systems, or need ongoing execution support, Growth (80-160 hours) provides more capacity. For one-time projects (website redesign, AI integration, video production), we will scope it as a standalone project. Not sure? Book a consultation — we will review your needs and recommend the right fit.',
                },
              ]}
              allowMultiple={false}
            />
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-32 px-6 border-t border-border">
          <div className="max-w-[1400px] mx-auto text-center">
            <h2 className="text-h2 mb-6">Ready to get started?</h2>
            <p className="text-body text-muted-foreground max-w-2xl mx-auto mb-12">
              Book a consultation to discuss your needs, or view our pricing to see how these services
              fit into our engagement models.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/book">
                <Button variant="primary" size="lg" className="w-full sm:w-auto border-glow">
                  Book Consultation
                </Button>
              </Link>
              <Link href="/pricing">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}